import React from 'react'
import styled from '@emotion/styled'
import { useInfiniteQuery } from '@tanstack/react-query'
import { queryClient } from '@core/Data/ReactQuery'
import { LoadingPlaceholder } from '@mobi/component-library/Misc/LoadingPlaceholder'
import { radius, spacing } from '@mobi/component-library/Foundations'
import { store } from '@core/Store'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import type { Tabs } from '@core/Areas/AccountActivity/types'
import { setShouldAllowPendingRacingTicketCountPolling } from '@core/Areas/AppHeader/Store'
import { AutoFetchButton } from '@core/Components/AutoFetchButton'
import { fetchPendingTicketsData } from '../helpers/api'

export const useAccountActivityData = ({
  activeTab,
  isOrderByEventStart,
  selectedDate,
}: {
  activeTab: Tabs
  isOrderByEventStart: boolean
  selectedDate?: string
}) => {
  const queryKey = queryKeys.accountActivityData(activeTab, isOrderByEventStart, selectedDate)

  const { fetchNextPage, isFetchingNextPage, isLoading, isError, data, refetch, isSuccess } =
    useInfiniteQuery({
      queryKey,
      queryFn: ({ pageParam }) =>
        fetchPendingTicketsData({
          nextPage: pageParam,
          activeTab,
          isOrderByEventStart,
          selectedDate,
        }),
      initialPageParam: '',
      getNextPageParam: prevPage => {
        if (prevPage.isFinalPage) return undefined
        return prevPage._nextPage || null
      },
      staleTime: 1_000 * 60 * 2, // 2 mins
      // Ensure fresh data on entry, rather than invalidate in all locations where bets are placed
      refetchOnMount: 'always',
    })

  React.useEffect(() => {
    if (isSuccess) {
      if (activeTab === 'pending') {
        const hasPendingTickets = data.pages[0].sortedIds.length > 0
        store.dispatch(setShouldAllowPendingRacingTicketCountPolling(hasPendingTickets))
      }
    }
  }, [activeTab, data, isSuccess])

  const isFinalPage = data && data.pages.length > 0 && data.pages[data.pages.length - 1].isFinalPage
  const isEmptyState = isFinalPage && data.pages[0].sortedIds.length === 0

  return {
    data,
    isError,
    isLoading,
    isEmptyState,
    isFinalPage,
    refetch: () => {
      queryClient.removeQueries({ queryKey })
      refetch()
    },
    /** Will auto fetch next page when in view */
    LoadMoreButton: () => (
      <AutoFetchButton onClick={fetchNextPage} isFetching={isFetchingNextPage} />
    ),
    SkeletonLoader,
  }
}

// ===============
// Local Component
// ===============

const SkeletonLoader: React.FC = () => (
  <WrapperStyled>
    {Array(9)
      .fill(null)
      .map((_, i) => (
        <LoadingPlaceholder key={i} width='100%' height='10rem' borderRadius={radius.lg} />
      ))}
  </WrapperStyled>
)

const WrapperStyled = styled.div({
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.smx1,
})
